import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HubIcon from "@mui/icons-material/Hub";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StepHeader from "../../../components/StepHeader";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import CircleIcon from "@mui/icons-material/Circle";
import { useAppContext } from "../../../contexts/AppContext";
import { isSiteSelectable } from "../../../data/mockSites";
import { tokens } from "../../../theme";

/**
 * Phase F1 site picker injected into the configs scene.
 *
 * Outputs: AppContext.selectedSites — `{ site_id, capability_id }[]`.
 * The configs scene merges this list into the run-config payload as `sites[]`
 * when non-empty. When empty (or every selected row is incomplete), the
 * existing tabular flow is byte-identical.
 *
 * Filtering: `taskFilter` narrows the capability options to entries whose
 * `tasks_supported` includes the value. For F1 only the tabular path is
 * reachable, so the configs scene passes `taskFilter="tabular"`. F2 will
 * generalize this when imaging is unlocked.
 */
const STATUS_DOT = {
  online: "#0087B4",
  offline: "var(--fx-text-muted)",
  maintenance: "#B08D57",
  unknown: "var(--fx-border-strong)",
};

const SiteSelectionPanel = ({ taskFilter = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const {
    sites,
    siteCapabilities,
    selectedSites,
    setSelectedSites,
    sitesLoading,
  } = useAppContext();

  const safeSites = sites || [];
  const safeSelected = selectedSites || [];

  // Site has at least one capability whose tasks_supported includes the
  // current taskFilter. When no taskFilter is set, every site qualifies.
  const siteOffersCurrentTask = (siteId) => {
    if (!taskFilter) return true;
    const caps = siteCapabilities?.[siteId] || [];
    return caps.some((c) => (c.tasks_supported || []).includes(taskFilter));
  };

  // Eligibility for the current task = selectable site (online + non-denied)
  // AND at least one matching capability for the chosen taskFilter. This is
  // why an already-selected site can flip to ineligible when the user
  // changes the Processing Module above.
  const eligibleSitesById = useMemo(() => {
    const map = new Map();
    for (const s of safeSites) {
      if (isSiteSelectable(s) && siteOffersCurrentTask(s.site_id)) {
        map.set(s.site_id, s);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeSites, siteCapabilities, taskFilter]);

  const selectedIds = useMemo(() => new Set(safeSelected.map((s) => s.site_id)), [safeSelected]);

  // Sites that can be added now: selectable + not already added + offers a
  // capability for the current task.
  const addableSites = useMemo(
    () =>
      safeSites.filter(
        (s) =>
          isSiteSelectable(s) &&
          !selectedIds.has(s.site_id) &&
          siteOffersCurrentTask(s.site_id)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safeSites, selectedIds, siteCapabilities, taskFilter]
  );

  const filterCaps = (caps) => {
    if (!taskFilter) return caps || [];
    return (caps || []).filter((c) => (c.tasks_supported || []).includes(taskFilter));
  };

  // Pretty label for taskFilter (used in helper text + tooltips).
  const taskLabel = (() => {
    if (taskFilter === "tabular") return "Tabular";
    if (taskFilter === "imaging_classification") return "Imaging · Classification";
    if (taskFilter === "imaging_segmentation") return "Imaging · Segmentation";
    return taskFilter || "";
  })();

  const addSite = (siteId) => {
    if (!siteId || selectedIds.has(siteId)) return;
    setSelectedSites([...safeSelected, { site_id: siteId, capability_id: "" }]);
  };

  const removeSite = (siteId) => {
    setSelectedSites(safeSelected.filter((s) => s.site_id !== siteId));
  };

  const setCapability = (siteId, capabilityId) => {
    setSelectedSites(
      safeSelected.map((s) => (s.site_id === siteId ? { ...s, capability_id: capabilityId } : s))
    );
  };

  const clearAll = () => setSelectedSites([]);

  return (
    <Card sx={{ mb: 2.5, p: 2.5, border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)', bgcolor: 'var(--fx-paper)' }}>
      <Box>
        <StepHeader
          number={2}
          title="Select client sites"
          subtitle="Pick the sites that will contribute their local datasets to this run. Each site keeps its data on-premises."
          action={
            safeSelected.length > 0 ? (
              <Button
                size="small"
                onClick={clearAll}
                startIcon={<CloseIcon fontSize="small" />}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Clear Selection
              </Button>
            ) : null
          }
        />

        {/* Adder row */}
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={openMenu}
            disabled={sitesLoading || addableSites.length === 0}
            startIcon={<AddIcon fontSize="small" />}
            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: colors.secondary[500],
              borderRadius: "10px",
              px: 1.75,
              py: 0.9,
              border: `1px dashed ${colors.secondary[500]}66`,
              bgcolor: "transparent",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: `${colors.secondary[500]}14`,
                border: `1px dashed ${colors.secondary[500]}`,
              },
              "&.Mui-disabled": {
                color: "var(--fx-text-muted)",
                border: "1px dashed var(--fx-border)",
              },
            }}
          >
            {sitesLoading ? "Loading sites…" : "Add a site"}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 280, borderRadius: 2, boxShadow: "var(--fx-shadow-md)" } } }}
          >
            {addableSites.map((site) => (
              <MenuItem
                key={site.site_id}
                onClick={() => {
                  addSite(site.site_id);
                  closeMenu();
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircleIcon sx={{ fontSize: 10, color: STATUS_DOT[site.status] || STATUS_DOT.unknown }} />
                  <Typography variant="body2">{site.display_name}</Typography>
                  {site.organization_display_name && (
                    <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                      · {site.organization_display_name}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Menu>

          {addableSites.length === 0 && safeSites.length > 0 && !sitesLoading && (
            <FormHelperText sx={{ mt: 1, ml: 0.5 }}>
              {taskFilter
                ? `All eligible sites for ${taskLabel} are already in the list, or the remaining sites don't currently offer this task.`
                : "All selectable sites are already in the list, or the remaining sites are offline / under maintenance / require access."}
            </FormHelperText>
          )}
        </Box>

        {/* Selection rows */}
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {safeSelected.length === 0 && (
            <Typography variant="body2" sx={{ color: "var(--fx-text-secondary)" }}>
              No sites selected yet. The run will use the default client count.
            </Typography>
          )}
          {safeSelected.map((row) => {
            const site = safeSites.find((s) => s.site_id === row.site_id);
            const eligible = !!(site && eligibleSitesById.has(row.site_id));
            const caps = filterCaps(siteCapabilities?.[row.site_id]);
            const capIncomplete = !row.capability_id;
            return (
              <Stack
                key={row.site_id}
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "center" }}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: eligible ? "rgba(0, 135, 180, 0.05)" : "rgba(176, 141, 87, 0.08)",
                  border: `1px solid ${eligible ? colors.secondary[200] : "rgba(176, 141, 87, 0.4)"}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <CircleIcon
                    sx={{ fontSize: 10, color: STATUS_DOT[site?.status] || STATUS_DOT.unknown, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--fx-text-primary)' }} noWrap>
                      {site?.display_name || row.site_id}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                      {site?.organization_display_name || row.site_id}
                    </Typography>
                  </Box>
                  {!eligible && (
                    <Tooltip
                      title={
                        site && isSiteSelectable(site) && taskFilter && !siteOffersCurrentTask(site.site_id)
                          ? `Site does not offer ${taskLabel} capabilities, remove it or switch the Processing Module above.`
                          : "Site became ineligible, offline, in maintenance, or access denied."
                      }
                    >
                      <LockIcon sx={{ fontSize: 16, color: colors.grey[500] }} />
                    </Tooltip>
                  )}
                </Stack>

                <FormControl size="small" sx={{ minWidth: 260 }} disabled={!eligible || caps.length === 0}>
                  <InputLabel id={`cap-${row.site_id}-label`}>Dataset capability</InputLabel>
                  <Select
                    labelId={`cap-${row.site_id}-label`}
                    label="Dataset capability"
                    value={row.capability_id || ""}
                    onChange={(e) => setCapability(row.site_id, e.target.value)}
                  >
                    {caps.length === 0 && (
                      <MenuItem value="" disabled>
                        {taskFilter
                          ? `No capabilities for task: ${taskFilter}`
                          : "No capabilities advertised"}
                      </MenuItem>
                    )}
                    {caps.map((cap) => (
                      <MenuItem key={cap.capability_id} value={cap.capability_id}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">{cap.display_name}</Typography>
                          <Chip
                            size="small"
                            label={cap.dataset_type}
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              bgcolor: colors.primary[100],
                              color: colors.primary[700],
                            }}
                          />
                          {cap.readiness_status && cap.readiness_status !== "ready" && (
                            <Chip
                              size="small"
                              label={cap.readiness_status}
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(176, 141, 87, 0.15)",
                                color: "#8a6a3a",
                              }}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {capIncomplete && eligible && (
                    <FormHelperText>Select a capability to include this site in the payload.</FormHelperText>
                  )}
                </FormControl>

                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {!capIncomplete && eligible && (
                    <Tooltip title="Ready to submit" placement="top">
                      <CheckCircleIcon sx={{ color: colors.secondary[500] }} />
                    </Tooltip>
                  )}
                  <Tooltip title="Remove from selection" placement="top">
                    <IconButton size="small" onClick={() => removeSite(row.site_id)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Card>
  );
};

export default SiteSelectionPanel;
